// src/top50/staff.js — Top 50 staff-facing tab (view, render, selectors, AI review, store rank)
//
// Path-to-8 Stage 5 / Phase 2c (May 6 2026): the Top 50 staff-side
// functions extracted from index.html (scattered between lines
// 9036-10115). Module state previously inline now module-private.
//
// State:
//   _top50Reports         — list of available weeks (loaded once, cached)
//   _top50CurrentReportId — selected week
//   _top50CurrentMetric   — 'value' | 'volume'
//   _top50CurrentStore    — selected store name
//   _top50Cache           — { reportId+metric: [entries] } loaded on demand
//
// Behaviour:
//   - First open: fetch reports list, default to latest week + current store + value
//   - Switching store/metric/week refetches if not cached, then re-renders
//   - Cross-store visible: store dropdown lists ALL stores in the report
//
// Free variables: DB (database wrapper from db.js, declared as `const DB`
// at top level — accessible as a bare identifier from sibling classic
// scripts including this IIFE bundle, but NOT as window.DB), and
// currentStore (declared as `let currentStore` in index.html, same
// scoping rules). Both are looked up at runtime via the global lexical
// scope. Do NOT prefix with `window.` — that's a property lookup on the
// global object and `let`/`const` top-level bindings don't attach there.
//
// Public API consumed by inline HTML (currently the inline copies handle
// onclick handlers; Phase 3 cutover will switch to Top50.staff.* calls):
//   Top50.staff.onTabOpen()
//   Top50.staff.onWeekChange()
//   Top50.staff.setMetric(metric)
//   Top50.staff.generateAiReview()
//   Top50.staff.loadStoreRank()

import {
  escHtml,
  escAttr,
  top50ClassBadges,
  normTop50StoreName,
  formatTop50DateShort,
  buildTop50AliasMap,
  findTop50StoreKey
} from './shared.js';

// ── Module-level state (private to this module) ───────
var _top50Reports = null;
var _top50CurrentReportId = null;
var _top50CurrentMetric = 'value';
var _top50CurrentStore = null;
var _top50Cache = {};
var _top50SortMode = 'rank';   // 'rank' | 'category' — how the report rows are ordered

export async function onTabOpen() {
  if (!_top50Reports) {
    document.getElementById('top50Result').innerHTML =
      '<div style="padding:40px 14px;text-align:center;color:var(--sub)">Loading Top 50 reports...</div>';
    try {
      var res = await fetch(DB.workerUrl() + '/api/top50/reports', { cache: 'no-store' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      _top50Reports = await res.json();
    } catch (err) {
      document.getElementById('top50Result').innerHTML =
        '<div style="padding:40px 14px;text-align:center;color:var(--sub)">Couldn\'t load reports.<br><span style="font-size:11px">' + (err.message || err) + '</span></div>';
      return;
    }
  }
  if (!_top50Reports || _top50Reports.length === 0) {
    document.getElementById('top50Result').innerHTML =
      '<div style="padding:40px 14px;text-align:center;color:var(--sub)">No Top 50 reports uploaded yet.<br><span style="font-size:11px">Reports are uploaded weekly by Head Office.</span></div>';
    return;
  }
  // Populate week dropdown (newest first — already sorted by API)
  var weekSel = document.getElementById('top50WeekSelect');
  weekSel.innerHTML = _top50Reports.map(function(r){
    return '<option value="' + r.id + '">' +
      formatTop50DateShort(r.week_start) + ' → ' + formatTop50DateShort(r.week_end) + '</option>';
  }).join('');
  if (!_top50CurrentReportId) _top50CurrentReportId = _top50Reports[0].id;
  weekSel.value = String(_top50CurrentReportId);

  // v81.5 — clear the active store so loadAndRender re-defaults it to the
  // preferred store (_kpiCurrentStore || currentStore). Without this reset,
  // an area manager who changes the KPI page's store dropdown and then
  // clicks Top 50 sees whichever store they were last looking at on Top 50,
  // not the one they're currently viewing on the KPI page. Per Matt's call
  // (May 11): "stores should be able to see other stores but it should
  // default on loading to their store" — i.e. cross-store browse is fine
  // mid-session, but each fresh tab open resets the default.
  _top50CurrentStore = null;

  // Populate store dropdown — defer until we have the entries (we read store
  // names from the first entry's store_data keys)
  await loadAndRender();
}

export function onWeekChange() {
  _top50CurrentReportId = parseInt(document.getElementById('top50WeekSelect').value, 10);
  loadAndRender();
}

export function setMetric(m) {
  _top50CurrentMetric = m;
  document.getElementById('top50BtnValue').style.background  = m === 'value'  ? 'var(--green)' : 'var(--card)';
  document.getElementById('top50BtnValue').style.color       = m === 'value'  ? '#fff' : 'var(--sub)';
  document.getElementById('top50BtnVolume').style.background = m === 'volume' ? 'var(--green)' : 'var(--card)';
  document.getElementById('top50BtnVolume').style.color      = m === 'volume' ? '#fff' : 'var(--sub)';
  loadAndRender();
}

async function loadAndRender() {
  if (!_top50CurrentReportId) return;
  var key = _top50CurrentReportId + '_' + _top50CurrentMetric;
  if (!_top50Cache[key]) {
    document.getElementById('top50Result').innerHTML =
      '<div style="padding:40px 14px;text-align:center;color:var(--sub)">Loading...</div>';
    try {
      var res = await fetch(DB.workerUrl() + '/api/top50/entries?report_id=' +
        _top50CurrentReportId + '&metric=' + _top50CurrentMetric, { cache: 'no-store' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      _top50Cache[key] = await res.json();
    } catch (err) {
      document.getElementById('top50Result').innerHTML =
        '<div style="padding:40px 14px;text-align:center;color:var(--sub)">Couldn\'t load: ' + (err.message || err) + '</div>';
      return;
    }
  }

  // v80.4 — Week-on-week movement. Find the report immediately before
  // the current one (reports list is sorted desc by week_start) and load
  // its entries for this metric. Best-effort: failure is non-fatal,
  // we just show no WoW chips. Cached the same way as current week.
  var idx = (_top50Reports || []).findIndex(function(r) { return r.id === _top50CurrentReportId; });
  var prevReport = (idx >= 0 && idx + 1 < _top50Reports.length) ? _top50Reports[idx + 1] : null;
  if (prevReport) {
    var prevKey = prevReport.id + '_' + _top50CurrentMetric;
    if (!_top50Cache[prevKey]) {
      try {
        var prevRes = await fetch(DB.workerUrl() + '/api/top50/entries?report_id=' +
          prevReport.id + '&metric=' + _top50CurrentMetric, { cache: 'no-store' });
        if (prevRes.ok) {
          _top50Cache[prevKey] = await prevRes.json();
        } else {
          _top50Cache[prevKey] = [];
        }
      } catch (e) {
        // Non-fatal — render proceeds without WoW chips
        console.warn('[top50] previous-week fetch failed:', e);
        _top50Cache[prevKey] = [];
      }
    }
  }

  var entries = _top50Cache[key];

  // Populate the store dropdown from the first entry's store_data keys
  // (all entries share the same set of stores)
  var storeSel = document.getElementById('top50StoreSelect');
  if (entries.length > 0) {
    var stores = Object.keys(entries[0].store_data || {}).sort();

    // Populate options once. Subsequent renders preserve them so a user
    // browsing other stores keeps the dropdown intact across week/metric
    // changes within the same tab session.
    if (storeSel.options.length === 0) {
      storeSel.innerHTML = stores.map(function(s){
        var label = s.replace(/^ELC - /i, '');
        return '<option value="' + escAttr(s) + '">' + escHtml(label) + '</option>';
      }).join('');
    }

    // v80.4 default-store fix: store_data keys use the spreadsheet form
    // ("ELC - DUBAI - DUBAI HILLS", uppercase, with "ELC -" prefix) while
    // currentStore comes from staff.store → stores.name in mixed case
    // without the prefix ("Dubai - Dubai Hills"). Exact-match was failing
    // and silently picking the alphabetically-first store. Normalise both
    // sides for the comparison; keep the original key for the option value.
    //
    // v81.5 default-store fix: prefer _kpiCurrentStore (KPI screen's
    // dropdown selection — what the area manager is actually looking at)
    // over currentStore (Rota screen state, which defaults to first store
    // on login). The fallback to currentStore handles single-store users
    // where _kpiCurrentStore mirrors currentStore anyway.
    //
    // The default is recomputed only when _top50CurrentStore is null —
    // which onTabOpen sets to force a fresh resolution. Once set, user
    // dropdown picks and week/metric changes preserve the active store
    // until the next tab open.
    if (!_top50CurrentStore) {
      var preferredStore = (typeof _kpiCurrentStore !== 'undefined' && _kpiCurrentStore)
        ? _kpiCurrentStore
        : (typeof currentStore !== 'undefined' ? currentStore : '');
      var defaultStore = stores[0];
      if (preferredStore) {
        // v85.0 — findTop50StoreKey consults stores.upload_name first
        // (the explicit alias) and falls back to normTop50StoreName only
        // when no alias is set. Matches the renderTradeDrives path in
        // index.html and the rank widget below.
        var allStoresList = (typeof ALL_STORES !== 'undefined') ? ALL_STORES : [];
        var aliasMap = buildTop50AliasMap(allStoresList);
        var match = findTop50StoreKey(preferredStore, aliasMap, stores);
        if (match) defaultStore = match;
      }
      _top50CurrentStore = defaultStore;
    }

    // Sync the select to the active store every render. Handles fresh-
    // default (above branch fired) and the rare case where a previously-
    // selected store has dropped out of a newly-loaded week's data.
    storeSel.value = _top50CurrentStore;
  } else {
    _top50CurrentStore = storeSel.value;
  }

  render();
}

// Sort-mode toggle (rank vs product category). Re-renders in place.
// Exposed as Top50.staff.setSort — called by the sort buttons rendered below.
export function setSort(mode) {
  _top50SortMode = (mode === 'category') ? 'category' : 'rank';
  render();
}

// Exported as Top50.staff.render so the store-select onchange handler
// (in HTML) can call it directly after cutover. Internal calls use the
// local `render` reference; both end up at the same function.
export function render() {
  var key = _top50CurrentReportId + '_' + _top50CurrentMetric;
  var entries = _top50Cache[key] || [];
  var store = document.getElementById('top50StoreSelect').value;
  _top50CurrentStore = store;

  if (!entries.length) {
    document.getElementById('top50Result').innerHTML =
      '<div style="padding:40px 14px;text-align:center;color:var(--sub)">No data.</div>';
    return;
  }

  // Build a per-row view of the chosen store's qty + soh, ranked by the
  // chosen metric's overall rank (entries are already sorted by rank).
  // Filter to entries where the store actually has data — should be all
  // of them in practice, but guard for partial uploads.
  var rows = entries.filter(function(e){
    return e.store_data && Object.prototype.hasOwnProperty.call(e.store_data, store);
  });

  // ─── Sort-by-category (store/HO feedback) ───
  // The product "class" (class_uae / class_qatar) is the merchandise category.
  // Numeric classes (100, 101…) are the categories; PROMO/CONSO/OOS are market
  // statuses. Use the class for the store's market (Qatar stores → class_qatar).
  function catVal(e) {
    var isQatar = /doha|qatar/i.test(store || '');
    return (isQatar ? e.class_qatar : e.class_uae) || (isQatar ? e.class_uae : e.class_qatar) || '';
  }
  function catKey(e) { var c = catVal(e); return (c === '' || c == null) ? '~' : String(c); }
  if (_top50SortMode === 'category') {
    rows = rows.slice().sort(function(a, b) {
      var ca = catKey(a), cb = catKey(b);
      var na = parseFloat(ca), nb = parseFloat(cb);
      var aNum = !isNaN(na) && /^\d/.test(ca), bNum = !isNaN(nb) && /^\d/.test(cb);
      if (aNum && bNum) { if (na !== nb) return na - nb; }   // numeric categories ascending
      else if (aNum !== bNum) { return aNum ? -1 : 1; }      // numeric classes before text
      else if (ca !== cb) { return ca < cb ? -1 : 1; }       // text classes alphabetically
      return a.rank - b.rank;                                 // within a category, by rank
    });
  }

  var totalCol = _top50CurrentMetric === 'value' ? 'Total Value' : 'Total Qty';

  // ─── v80.4 — Build week-on-week + chain-average lookups ───
  // Previous week's entries for this metric (loaded by loadAndRender).
  // We index by item_code for O(1) lookup during rendering.
  var idx = (_top50Reports || []).findIndex(function(r) { return r.id === _top50CurrentReportId; });
  var prevReport = (idx >= 0 && idx + 1 < _top50Reports.length) ? _top50Reports[idx + 1] : null;
  var prevByCode = null;
  var prevReportLoaded = false;
  if (prevReport) {
    var prevEntries = _top50Cache[prevReport.id + '_' + _top50CurrentMetric];
    // prevEntries can be [] (loaded but empty) or undefined (load failed/skipped).
    // We only treat "loaded" as true if it's an array — empty array still counts
    // because that means we DID look and there was no data → "NEW" is meaningful.
    if (Array.isArray(prevEntries)) {
      prevReportLoaded = true;
      prevByCode = {};
      prevEntries.forEach(function(pe) {
        if (pe.item_code) prevByCode[pe.item_code] = pe.rank;
      });
    }
  }
  // Store count drives the chain-average comparison: chain_avg = total_qty / N.
  // Pulled from the first entry's store_data because that's the source of truth
  // for "stores that participated in this report" — robust to future expansion.
  var storeCount = (entries[0] && entries[0].store_data) ? Object.keys(entries[0].store_data).length : 28;

  // ─── AI Review panel (collapsed; expands on click) ───
  // Cached per (report, store). First click fires Anthropic; subsequent
  // clicks for the same week + store hit the cache (free).
  var html = '<div id="top50AiReviewPanel" style="background:linear-gradient(135deg,var(--stores-ok-soft) 0%,var(--card) 100%);border:1px solid var(--border);border-radius:12px;padding:12px 14px;margin-bottom:12px">';
  html += '<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap">';
  html += '<div style="font-size:12px;color:var(--sub);font-weight:600;text-transform:uppercase;letter-spacing:.5px">✨ AI Review</div>';
  html += '<button id="top50AiBtn" onclick="Top50.staff.generateAiReview()" style="background:var(--green);color:#fff;border:none;border-radius:6px;padding:6px 14px;font-size:12px;font-weight:600;cursor:pointer;font-family:inherit">Generate review for ' + escHtml(store.replace(/^ELC - /, '')) + '</button>';
  html += '</div>';
  html += '<div id="top50AiResult" style="display:none;margin-top:10px;padding:10px 12px;background:var(--card);border:1px solid var(--border);border-radius:8px;font-size:13px;line-height:1.55;white-space:pre-wrap;color:var(--text)"></div>';
  html += '<div id="top50AiMeta" style="display:none;margin-top:6px;font-size:11px;color:var(--sub)"></div>';
  html += '</div>';

  // ─── Sort toggle: By Rank | By Category ───
  function sortBtn(mode, label) {
    var active = _top50SortMode === mode;
    return '<button onclick="Top50.staff.setSort(\'' + mode + '\')" style="padding:6px 12px;border:none;' +
      (mode === 'category' ? 'border-inline-start:1px solid var(--border);' : '') +
      'background:' + (active ? 'var(--green)' : 'var(--card)') + ';color:' + (active ? '#fff' : 'var(--sub)') +
      ';font-size:12px;font-weight:600;cursor:pointer;font-family:inherit">' + label + '</button>';
  }
  html += '<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;flex-wrap:wrap">';
  html += '<span style="font-size:11px;color:var(--sub);font-weight:600">Sort:</span>';
  html += '<div style="display:inline-flex;border:1px solid var(--border);border-radius:6px;overflow:hidden">' +
          sortBtn('rank', 'By Rank') + sortBtn('category', 'By Category') + '</div>';
  html += '</div>';

  html += '<div style="overflow-x:auto;background:var(--card);border:1px solid var(--border);border-radius:12px">';
  html += '<table style="width:100%;border-collapse:collapse;font-size:12px">';
  html += '<thead><tr style="background:var(--green);color:#fff;position:sticky;top:0;z-index:1">';
  html += '<th style="padding:7px 8px;text-align:start">#</th>';
  html += '<th style="padding:7px 8px;text-align:start">Code</th>';
  html += '<th style="padding:7px 8px;text-align:start">Description</th>';
  html += '<th style="padding:7px 8px;text-align:end">Sold</th>';
  html += '<th style="padding:7px 8px;text-align:end">SOH</th>';
  // v126.9 (Doha - Landmark Mall UAT: "it would be best if warehouse stocks
  // can be added") — warehouse availability per line; the data already rides
  // every entry (uae_wh_soh / qatar_wh_soh — the AI review has used it since
  // v80.3). v126.83 (the Qatar area manager): BOTH markets show now, not just the store's
  // own — an AM comparing cross-country needs the pair side by side.
  html += '<th style="padding:7px 8px;text-align:end">UAE WH</th>';
  html += '<th style="padding:7px 8px;text-align:end">Qatar WH</th>';
  html += '<th style="padding:7px 8px;text-align:end">' + totalCol + '<div style="font-size:10px;font-weight:400;opacity:.85">chain total</div></th>';
  html += '<th style="padding:7px 8px;text-align:start">Class</th>';
  html += '</tr></thead><tbody>';

  var lastCat = null;
  rows.forEach(function(e) {
    // Category group header (only when sorting by category) so the grouping is
    // visible even for normal numeric classes (which carry no badge).
    if (_top50SortMode === 'category') {
      var thisCat = catKey(e);
      if (thisCat !== lastCat) {
        lastCat = thisCat;
        var catLabel = thisCat === '~' ? 'Uncategorised'
          : (/^\d/.test(thisCat) ? 'Class ' + thisCat : thisCat);
        html += '<tr style="background:var(--surface-3)"><td colspan="9" style="padding:6px 8px;font-size:11px;font-weight:700;color:var(--stores-slate);letter-spacing:.3px">' + escHtml(catLabel) + '</td></tr>';
      }
    }
    var sd = e.store_data[store] || { qty: 0, soh: 0 };
    var lowStock = (sd.qty != null && sd.soh != null && sd.qty > 0 && sd.soh < sd.qty);

    // ─── v80.4 chips ───
    // WoW rank movement — look up the same item_code in last week's entries.
    var wowChip = '';
    if (prevByCode && Object.prototype.hasOwnProperty.call(prevByCode, e.item_code)) {
      var delta = prevByCode[e.item_code] - e.rank;  // positive = improved
      if (delta > 0)      wowChip = '<div style="font-size:9px;font-weight:600;color:var(--stores-up);margin-top:2px">↑' + delta + '</div>';
      else if (delta < 0) wowChip = '<div style="font-size:9px;font-weight:600;color:var(--stores-down);margin-top:2px">↓' + Math.abs(delta) + '</div>';
      else                wowChip = '<div style="font-size:9px;font-weight:600;color:var(--stores-ink-3);margin-top:2px">=</div>';
    } else if (prevReportLoaded) {
      // We have last week's data and this item wasn't in it — genuinely new.
      // (Skipping the badge if last week is missing avoids a misleading "NEW"
      // chip for every row when there's no previous week to compare against.)
      wowChip = '<div style="font-size:9px;font-weight:700;color:var(--stores-blue);margin-top:2px">NEW</div>';
    }

    // vs chain average — chain avg = total_qty / store count, skip if chain qty is 0.
    // We compare per-store qty regardless of metric; per-store value isn't
    // available in the source data.
    var avgChip = '';
    var chainAvg = (e.total_qty && storeCount > 0) ? (e.total_qty / storeCount) : 0;
    if (chainAvg > 0 && sd.qty != null) {
      var pct = Math.round((sd.qty - chainAvg) / chainAvg * 100);
      // Hide tiny variance (within ±5%) — too noisy to be useful.
      if (Math.abs(pct) >= 5) {
        var avgColor = pct >= 0 ? 'var(--stores-up)' : 'var(--stores-down)';
        var avgArrow = pct >= 0 ? '▲' : '▼';
        avgChip = '<div style="font-size:9px;font-weight:600;color:' + avgColor + ';margin-top:2px">' + avgArrow + Math.abs(pct) + '% vs avg</div>';
      }
    }

    html += '<tr style="border-bottom:1px solid var(--surface-3)">';
    html += '<td style="padding:7px 8px;color:var(--sub);font-variant-numeric:tabular-nums;vertical-align:top"><div>' + e.rank + '</div>' + wowChip + '</td>';
    html += '<td style="padding:7px 8px;font-family:monospace;font-size:11px;vertical-align:top">' + escHtml(e.item_code || '—') + '</td>';
    html += '<td translate="no" style="padding:7px 8px;line-height:1.3;vertical-align:top">' + escHtml(e.description || '—') + '</td>';   // v135.1: product text from the file
    html += '<td style="padding:7px 8px;text-align:end;font-variant-numeric:tabular-nums;vertical-align:top"><div>' + (sd.qty != null ? sd.qty : '—') + '</div>' + avgChip + '</td>';
    html += '<td style="padding:7px 8px;text-align:end;font-variant-numeric:tabular-nums;vertical-align:top' + (lowStock ? ';color:var(--stores-down);font-weight:600' : '') + '">' + (sd.soh != null ? sd.soh : '—') + (lowStock ? ' ⚠' : '') + '</td>';
    // v126.83 — both markets' warehouse stock; the store's OWN market renders
    // solid, the other market stays muted so the local number still leads.
    var ownQatar = /^doha|qatar/i.test(String(store));
    var whUaeStyle = ownQatar ? 'color:var(--sub)' : 'color:inherit;font-weight:600';
    var whQatarStyle = ownQatar ? 'color:inherit;font-weight:600' : 'color:var(--sub)';
    html += '<td style="padding:7px 8px;text-align:end;' + whUaeStyle + ';font-variant-numeric:tabular-nums;vertical-align:top">' + (e.uae_wh_soh != null ? e.uae_wh_soh : '—') + '</td>';
    html += '<td style="padding:7px 8px;text-align:end;' + whQatarStyle + ';font-variant-numeric:tabular-nums;vertical-align:top">' + (e.qatar_wh_soh != null ? e.qatar_wh_soh : '—') + '</td>';
    var totalNum = _top50CurrentMetric === 'value' ? e.total_value : e.total_qty;
    html += '<td style="padding:7px 8px;text-align:end;color:var(--sub);font-variant-numeric:tabular-nums;vertical-align:top">' +
            (totalNum != null ? Number(totalNum).toLocaleString() : '—') + '</td>';
    html += '<td style="padding:7px 8px;vertical-align:top">' + top50ClassBadges(e.class_uae, e.class_qatar) + '</td>';
    html += '</tr>';
  });
  html += '</tbody></table></div>';

  // Footer: small legend
  html += '<div style="margin-top:10px;font-size:11px;color:var(--sub);line-height:1.7;padding:0 4px">';
  html += '<strong>Sold</strong> = units sold this week · <strong>SOH</strong> = current stock on hand · <strong>WH</strong> = warehouse stock available in your market · ';
  html += '<span style="color:var(--stores-down)">⚠</span> stock lower than weekly sell-through<br>';
  html += '<span style="color:var(--stores-up);font-weight:600">↑5</span>/<span style="color:var(--stores-down);font-weight:600">↓3</span>/<span style="color:var(--stores-blue);font-weight:700">NEW</span> = rank movement vs last week · ';
  html += '<span style="color:var(--stores-up);font-weight:600">▲X% vs avg</span> = ahead of chain average for that product<br>';
  html += '<span style="display:inline-block;background:var(--warn-bg);border:1px solid var(--stores-amber);color:var(--stores-amber);padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600">PROMO</span> = promo line · ';
  html += '<span style="display:inline-block;background:var(--surface-3);border:1px solid var(--stores-slate);color:var(--stores-slate);padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600">DISCONTINUED</span> = run-down stock, do not reorder';
  html += '</div>';

  document.getElementById('top50Result').innerHTML = html;
}

// ─── AI Review (v80.3) ─────────────────────────────────────────
// Click handler for the Generate button. Calls /api/top50/ai-review,
// shows loading state, renders result on success.
//
// The endpoint is cache-first on the server side, so the first user
// to ask for a given (store, week) pays the API cost; everyone else
// hits cache for free. UNIQUE constraint on (report_id, store) means
// max one Anthropic call per store per week.
export async function generateAiReview() {
  var btn = document.getElementById('top50AiBtn');
  var resultEl = document.getElementById('top50AiResult');
  var metaEl = document.getElementById('top50AiMeta');
  if (!btn || !resultEl || !metaEl) return;
  if (!_top50CurrentReportId || !_top50CurrentStore) return;

  btn.disabled = true;
  // v135.1: rebuilt in English, not read off the screen (with Arabic on, the screen holds the Arabic).
  var origLabel = 'Generate review for ' + String(_top50CurrentStore).replace(/^ELC - /, '');
  btn.textContent = '⏳ Thinking...';
  btn.style.opacity = '0.7';
  resultEl.style.display = 'none';
  metaEl.style.display = 'none';

  try {
    // v132.48 — Doha Landmark, via the Dev tab, 22 Sep 2026: "We are getting a
    // message: Couldn't generate review: staffid and PIN required", every time.
    //
    // The route needs an identity because each uncached click costs a real
    // Anthropic call (v131.39). authFieldsOrNull() returns null whenever there
    // is no live PIN session, which on a shared till is most of the time, so
    // the body went out with no identity and the Worker correctly refused it.
    //
    // Refusing was right; giving up was not. Every other staff-plane action
    // ASKS: requirePinForAttribution opens the staff picker and the PIN pad,
    // and db.js caches the verified PIN, so authFields() then succeeds. Same
    // gate, one extra tap, instead of a dead end the user cannot get past.
    var auth = (typeof DB !== 'undefined' && DB.authFieldsOrNull && DB.authFieldsOrNull()) || null;
    if (!auth && typeof requirePinForAttribution === 'function') {
      btn.textContent = '🔑 Verify PIN...';
      await requirePinForAttribution('generating the Top 50 review');
      auth = (DB.authFieldsOrNull && DB.authFieldsOrNull()) || null;
      btn.textContent = '⏳ Thinking...';
    }
    var res = await fetch(DB.workerUrl() + '/api/top50/ai-review', { cache: 'no-store',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({
        report_id: _top50CurrentReportId,
        store: _top50CurrentStore
      }, auth || {}))   // v131.39: PIN or Bearer
    });
    var data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || ('HTTP ' + res.status));
    }
    // Render the review text. Server returns plain text with "### Section"
    // headings — convert those to bold lines so the Markdown is readable.
    var text = data.review_text || '';
    var html = escHtml(text)
      .replace(/^###\s*(.+)$/gm, '<strong style="display:block;margin-top:8px;color:var(--rota-green-ink)">$1</strong>')
      .replace(/^-\s+/gm, '• ');
    resultEl.innerHTML = html;
    resultEl.style.display = 'block';

    // Meta line: when generated, by whom (if known), cache state
    var meta = data.cached
      ? '🗄 Cached review from ' + (data.generated_at ? new Date(data.generated_at).toLocaleString('en-GB',{dateStyle:'medium',timeStyle:'short'}) : '—')
      : '✨ Just generated · ' + (data.model || 'claude');
    metaEl.textContent = meta;
    metaEl.style.display = 'block';

    // Once shown, button becomes "Hide / Re-show" — keep label minimal
    btn.disabled = false;
    btn.textContent = '✓ Review shown';
    btn.style.opacity = '1';
    btn.style.background = 'var(--sub)';
  } catch (err) {
    btn.disabled = false;
    btn.textContent = origLabel;
    btn.style.opacity = '1';
    // v132.48 — cancelling the PIN pad is a decision, not a failure. It
    // rejects this promise, and shouting "Couldn't generate review" at
    // somebody who just pressed Cancel is noise.
    if (!err || err.message === undefined || /cancel/i.test(String(err && err.message))) {
      resultEl.style.display = 'none';
      return;
    }
    console.error('[top50/ai] failed:', err);
    resultEl.style.display = 'block';
    resultEl.style.color = 'var(--red)';
    resultEl.style.borderColor = 'var(--red)';
    resultEl.innerHTML = '⚠ Couldn\'t generate review: ' + escHtml(err.message || 'unknown error');
  }
}

// ─── Store rank widget (v80.6) ─────────────────────────────────
// Mounted on the KPI dashboard (kpiTop50RankSection). Loads the latest
// week's volume entries, sums per-store qty, ranks descending, highlights
// the current user's store. Failure is surfaced visibly so the user can
// see WHY data isn't appearing (no upload, network error, auth issue)
// rather than a silent empty section.
export async function loadStoreRank() {
  var mount = document.getElementById('kpiTop50RankSection');
  if (!mount) return;
  mount.innerHTML = '<div style="padding:16px;text-align:center;font-size:12px;color:var(--sub)">Loading Top 50 rank…</div>';

  try {
    // Get the list of reports if we don't have it cached
    if (!_top50Reports) {
      var rRes = await fetch(DB.workerUrl() + '/api/top50/reports', { cache: 'no-store' });
      if (rRes.ok) _top50Reports = await rRes.json();
    }
    if (!_top50Reports || _top50Reports.length === 0) {
      mount.innerHTML =
        '<div style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:14px 16px">' +
        '<div style="font-size:11px;color:var(--sub);font-weight:600;text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">📦 Top 50 Rank</div>' +
        '<div style="font-size:13px;color:var(--sub)">No Top 50 reports uploaded yet. Reports are uploaded weekly by Head Office.</div>' +
        '</div>';
      return;
    }

    // Use the most recent report (reports are returned newest-first)
    var latest = _top50Reports[0];
    var key = latest.id + '_volume';
    if (!_top50Cache[key]) {
      var eRes = await fetch(DB.workerUrl() + '/api/top50/entries?report_id=' + latest.id + '&metric=volume', { cache: 'no-store' });
      if (!eRes.ok) throw new Error('HTTP ' + eRes.status);
      _top50Cache[key] = await eRes.json();
    }
    var entries = _top50Cache[key];
    if (!entries.length) {
      mount.innerHTML = '';
      return;
    }

    // For each store, sum its qty across all top 50 products
    var stores = Object.keys(entries[0].store_data || {}).sort();
    var totals = {};
    stores.forEach(function(s){ totals[s] = 0; });
    entries.forEach(function(e) {
      Object.keys(e.store_data || {}).forEach(function(s) {
        if (totals[s] != null) {
          totals[s] += (e.store_data[s].qty || 0);
        }
      });
    });

    // Build ranking — descending by total qty
    var ranking = stores.map(function(s) { return { store: s, total: totals[s] }; })
      .sort(function(a, b) { return b.total - a.total; });

    // Chain stats — for the "vs avg" column
    var chainTotal = ranking.reduce(function(sum, r) { return sum + r.total; }, 0);
    var chainAvg = ranking.length ? chainTotal / ranking.length : 0;

    // Find the user's home store rank (using the same normalization fix as
    // the main Top 50 viewer — currentStore is "Dubai - Dubai Hills" while
    // store_data keys are "ELC - DUBAI - DUBAI HILLS").
    //
    // v81.5 — prefer _kpiCurrentStore (KPI dropdown selection) over
    // currentStore (Rota screen, defaults to first store on login). This
    // widget renders on the Ranking tab; for area managers, the "your
    // rank" callout has to match whichever store they're looking at on
    // the KPI page, not their login default.
    var preferredStore = (typeof _kpiCurrentStore !== 'undefined' && _kpiCurrentStore)
      ? _kpiCurrentStore
      : (typeof currentStore !== 'undefined' ? currentStore : '');
    var myStoreKey = null;
    if (preferredStore) {
      // v85.0 — alias-aware lookup (see comment in onTabOpen above).
      var allStoresList = (typeof ALL_STORES !== 'undefined') ? ALL_STORES : [];
      var aliasMap = buildTop50AliasMap(allStoresList);
      var match = findTop50StoreKey(preferredStore, aliasMap, stores);
      if (match) myStoreKey = match;
    }
    var myRankIdx = myStoreKey ? ranking.findIndex(function(r) { return r.store === myStoreKey; }) : -1;

    // ─── Render ───
    var rankHtml = '<div style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:14px 16px">';
    rankHtml += '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;flex-wrap:wrap;gap:6px">';
    rankHtml += '<div style="font-size:11px;color:var(--sub);font-weight:600;text-transform:uppercase;letter-spacing:.5px">📦 Top 50 Rank — week of ' + escHtml(formatTop50DateShort(latest.week_start)) + '</div>';
    if (myRankIdx >= 0) {
      var myColor = myRankIdx < ranking.length / 3 ? 'var(--stores-up)' :
                    myRankIdx < (2 * ranking.length / 3) ? 'var(--stores-amber)' : 'var(--stores-down)';
      rankHtml += '<div style="font-size:13px;font-weight:700;color:' + myColor + '">' +
                  'Your rank: #' + (myRankIdx + 1) + ' of ' + ranking.length + '</div>';
    }
    rankHtml += '</div>';
    rankHtml += '<div style="font-size:11px;color:var(--sub);margin-bottom:8px;line-height:1.5">' +
      'Total units sold across all 50 best sellers this week. ' +
      'Higher = stronger sell-through on the chain\'s top performers.</div>';

    rankHtml += '<div style="overflow-x:auto"><table style="width:100%;border-collapse:collapse;font-size:12px">';
    rankHtml += '<thead><tr style="background:var(--surface-2);border-bottom:2px solid var(--border)">';
    rankHtml += '<th style="padding:6px 8px;text-align:start">#</th>';
    rankHtml += '<th style="padding:6px 8px;text-align:start">Store</th>';
    rankHtml += '<th style="padding:6px 8px;text-align:end">Top 50 Units</th>';
    rankHtml += '<th style="padding:6px 8px;text-align:end">vs Chain Avg</th>';
    rankHtml += '</tr></thead><tbody>';
    ranking.forEach(function(r, i) {
      var pct = chainAvg > 0 ? Math.round((r.total - chainAvg) / chainAvg * 100) : 0;
      var pctColor = pct >= 0 ? 'var(--stores-up)' : 'var(--stores-down)';
      var pctArrow = pct >= 0 ? '▲' : '▼';
      var isMe = (r.store === myStoreKey);
      rankHtml += '<tr style="border-bottom:1px solid var(--surface-3)' + (isMe ? ';background:var(--stores-ok-soft);font-weight:600' : '') + '">';
      rankHtml += '<td style="padding:6px 8px;font-variant-numeric:tabular-nums">' + (i + 1) + '</td>';
      rankHtml += '<td style="padding:6px 8px">' + escHtml(r.store.replace(/^ELC - /, '')) + (isMe ? ' <span style="font-size:10px;color:var(--rota-green-ink);margin-inline-start:4px">(you)</span>' : '') + '</td>';
      rankHtml += '<td style="padding:6px 8px;text-align:end;font-variant-numeric:tabular-nums">' + r.total.toLocaleString() + '</td>';
      rankHtml += '<td style="padding:6px 8px;text-align:end;font-variant-numeric:tabular-nums;color:' + pctColor + ';font-weight:600">' + pctArrow + Math.abs(pct) + '%</td>';
      rankHtml += '</tr>';
    });
    rankHtml += '</tbody></table></div>';
    rankHtml += '</div>';

    mount.innerHTML = rankHtml;
  } catch (err) {
    console.warn('[top50-rank] failed to load:', err);
    // v80.6: surface the failure visibly. Silently hiding the section was
    // confusing — if the data isn't there, the user should see WHY (no
    // upload, network error, auth issue) so they can ask the right
    // follow-up question.
    mount.innerHTML =
      '<div style="background:var(--card);border:1px solid var(--border);border-radius:12px;padding:14px 16px">' +
      '<div style="font-size:11px;color:var(--sub);font-weight:600;text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">📦 Top 50 Rank</div>' +
      '<div style="font-size:13px;color:var(--red)">Couldn\'t load: ' + escHtml(err.message || String(err)) + '</div>' +
      '</div>';
  }
}
